import dayjs from "dayjs";

const MONTH_NAMES = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

const formatDate = (inputDate: string | number | Date): string => {
  const date = new Date(inputDate);
  const today = new Date();

  const isToday =
    date.getDate() === today.getDate() &&
    date.getMonth() === today.getMonth() &&
    date.getFullYear() === today.getFullYear();

  if (isToday) return "Today";

  const monthName = MONTH_NAMES[date.getMonth()];
  return `${monthName} ${date.getDate()}`;
};

export const formatHistoryDate = (
  inputDate: string | number | Date,
): string => {
  const date = new Date(inputDate);
  // An unset or unparseable timestamp would otherwise render as
  // "undefined NaN, NaN", since MONTH_NAMES[NaN] is undefined.
  if (Number.isNaN(date.getTime())) {
    return "Recent";
  }

  const today = new Date();
  const yesterday = new Date();
  yesterday.setDate(today.getDate() - 1);

  // Check if date is today
  if (date.toDateString() === today.toDateString()) {
    return "Today";
  }

  // Check if date is yesterday
  if (date.toDateString() === yesterday.toDateString()) {
    return "Yesterday";
  }

  const monthName = MONTH_NAMES[date.getMonth()];
  const day = date.getDate();

  // If same year, show only month and day
  if (date.getFullYear() === today.getFullYear()) {
    return `${monthName} ${day}`;
  }

  // If different year, show month, day and year
  return `${monthName} ${day}, ${date.getFullYear()}`;
};

export const groupNotificationsByDateAndType = (
  notifications: Array<{
    notification_data?: { at?: string | number | Date };
    type: string;
  }>,
): Record<string, Record<string, unknown[]>> => {
  return notifications.reduce<Record<string, Record<string, unknown[]>>>(
    (groups, notification) => {
      const date = isCurrentYear(notification.notification_data?.at)
        ? formatDate(
            notification.notification_data?.at as string | number | Date,
          )
        : formatHistoryDate(
            notification.notification_data?.at as string | number | Date,
          );
      if (!groups[date]) groups[date] = {};
      if (!groups[date][notification.type])
        groups[date][notification.type] = [];
      groups[date][notification.type].push(notification);
      return groups;
    },
    {},
  );
};

export const getTime = (date: string | number | Date): string => {
  return dayjs(date).format("hh:mm A");
};

const isCurrentYear = (
  inputDate: string | number | Date | undefined,
): boolean => {
  const date = new Date(inputDate as string | number | Date);
  const today = new Date();
  return date.getFullYear() === today.getFullYear();
};

export const formatISODateToYYYYMMDD = (
  dateString: string | null | undefined,
): string => {
  if (!dateString) return "Invalid Date";
  return dayjs(dateString).format("YYYY-MM-DD");
};

export const formattedDate = (
  timestamp: string | number | Date | null | undefined,
  locale: string = "en-US",
): string => {
  if (!timestamp) return "No date available";
  try {
    const date = new Date(timestamp);
    const today = new Date();
    const isToday =
      date.getDate() === today.getDate() &&
      date.getMonth() === today.getMonth() &&
      date.getFullYear() === today.getFullYear();

    return isToday ? "Today" : date.toLocaleDateString(locale);
  } catch {
    return "Invalid date";
  }
};

export const formatDateWithMicroseconds = (date: Date): string => {
  const pad = (n: number, z: number = 2): string => ("00" + n).slice(-z);
  const year = date.getFullYear();
  const month = pad(date.getMonth() + 1);
  const day = pad(date.getDate());
  const hours = pad(date.getHours());
  const minutes = pad(date.getMinutes());
  const seconds = pad(date.getSeconds());
  const milliseconds = ("00" + date.getMilliseconds()).slice(-3);
  // JS doesn't provide microseconds, so pad with 3 zeros
  return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}.${milliseconds}000`;
};

export function formatISODateToMMDDYYYY(
  dateString: string | null | undefined,
): string {
  if (!dateString) return "Invalid Date";

  const date = new Date(dateString);
  if (isNaN(date.getTime())) return "Invalid Date";

  const month = (date.getMonth() + 1).toString().padStart(2, "0");
  const day = date.getDate().toString().padStart(2, "0");
  const year = date.getFullYear();

  return `${month}/${day}/${year}`;
}

export const formatDateToYYYYMMDD = (
  date:
    | string
    | number
    | Date
    | { format: (fmt: string) => string }
    | null
    | undefined,
): string | null => {
  if (!date) return null;

  // Handle Dayjs objects (from date pickers)
  if ((date as { format?: (fmt: string) => string })?.format) {
    return (date as { format: (fmt: string) => string }).format("YYYY-MM-DD");
  }

  // Handle Date objects or ISO strings
  const dateObj = new Date(date as string | number | Date);
  if (isNaN(dateObj.getTime())) return null;

  return dateObj.toISOString().split("T")[0]; // Gets YYYY-MM-DD part
};

export const formatDateText = (date: string | number | Date): string => {
  if (!date) return "-";
  return formatDate(date) + " at " + getTime(date);
};

export const formatLongDate = (
  dateString: string | number | Date,
  locale: string = "en-US",
): string =>
  new Date(dateString).toLocaleDateString(locale, {
    month: "long",
    day: "numeric",
    year: "numeric",
  });

export function formatDateLong(
  dateString: string | number | Date,
  locale: string = "en-US",
): string {
  const time = new Date(dateString).toLocaleTimeString(locale, {
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });

  return `${time} | ${formatLongDate(dateString, locale)}`;
}

export const formatDateTimeWithAt = (
  dateString: string | number | Date,
): string => {
  return dayjs(dateString).format("MM/DD/YYYY [at] hh:mm A");
};
